import z from "zod";
import React, { useState, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm, useFieldArray, Controller } from "react-hook-form";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
import { motion } from "framer-motion";

import type { RootState } from "@/store";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import ImageUpload from "@/components/common/ImageUpload";
import {
  ArrowLeft,
  Plus,
  X,
  Info,
  Users,
  FileText,
  AlertCircle,
  CheckCircle2,
  HelpCircle,
  GraduationCap,
  Calendar,
  Trophy,
} from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

import {
  useScholarship,
  useUpdateScholarship,
} from "@/services/scholarshipService";

// Zod validation schema
const scholarshipEditSchema = z.object({
  status: z.string().min(1, "Status is required"),
  translations: z
    .array(
      z.object({
        id: z.number().optional(),
        locale: z.string().min(1, "Locale is required"),
        title: z.string().min(1, "Title is required"),
        description: z.string().optional(),
        aim: z.string().optional(),
        eligibility: z.string().optional(),
        offers: z.string().optional(),
        slug: z.string().min(1, "Slug is required"),
      })
    )
    .min(1, "At least one translation is required"),
  sessions: z
    .array(
      z.object({
        id: z.number().optional(),
        academic_year: z.string().min(1, "Academic year is required"),
        is_active: z.boolean(),
        gallery: z.array(z.string()).optional(),
        sponsors: z
          .array(
            z.object({
              id: z.number().optional(),
              name: z.string().min(1, "Sponsor name is required"),
              title: z.string().optional(),
              bio: z.string().optional(),
              picture: z.string().optional(),
              is_main: z.boolean(),
            })
          )
          .optional(),
        beneficiaries: z
          .array(
            z.object({
              id: z.number().optional(),
              name: z.string().min(1, "Beneficiary name is required"),
              picture: z.string().optional(),
              school: z.string().optional(),
              location: z.string().optional(),
              class: z.string().optional(),
              notes: z.string().optional(),
            })
          )
          .optional(),
      })
    )
    .min(1, "At least one session is required"),
});

type ScholarshipEditFormData = z.infer<typeof scholarshipEditSchema>;

const quillModules = {
  toolbar: [
    [{ header: [1, 2, false] }],
    ["bold", "italic", "underline", "strike", "blockquote"],
    [
      { list: "ordered" },
      { list: "bullet" },
      { indent: "-1" },
      { indent: "+1" },
    ],
    ["link"],
    ["clean"],
  ],
};

const ScholarshipEdit: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { currentLanguage } = useSelector((state: RootState) => state.language);
  const scholarshipId = parseInt(id || "0");

  const { data: scholarship, isLoading: isLoadingScholarship } =
    useScholarship(scholarshipId);
  const updateScholarshipMutation = useUpdateScholarship();
  const [validationError, setValidationError] = useState("");

  const {
    register,
    control,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
    reset,
  } = useForm<ScholarshipEditFormData>({
    resolver: zodResolver(scholarshipEditSchema),
    defaultValues: {
      status: "active",
      translations: [],
      sessions: [],
    },
  });

  const {
    fields: translationFields,
    append: appendTranslation,
    remove: removeTranslation,
  } = useFieldArray({
    control,
    name: "translations",
  });

  const {
    fields: sessionFields,
    append: appendSession,
    remove: removeSession,
  } = useFieldArray({
    control,
    name: "sessions",
  });

  const [autoGenerateSlugs, setAutoGenerateSlugs] = useState<{
    [key: string]: boolean;
  }>({});

  // Pre-populate form when scholarship data is loaded
  useEffect(() => {
    if (scholarship) {
      reset({
        status: scholarship.status || "active",
        translations: scholarship.translations?.map((t) => ({
          id: t.id,
          locale: t.locale,
          title: t.title,
          description: t.description || "",
          aim: t.aim || "",
          eligibility: t.eligibility || "",
          offers: t.offers || "",
          slug: t.slug,
        })) || [],
        sessions: scholarship.sessions?.map((s) => ({
          id: s.id,
          academic_year: s.academic_year,
          is_active: !!s.is_active,
          gallery: s.gallery || [],
          sponsors: s.sponsors?.map((sp) => ({
            id: sp.id,
            name: sp.name,
            title: sp.title || "",
            bio: sp.bio || "",
            picture: sp.picture || "",
            is_main: !!sp.is_main,
          })) || [],
          beneficiaries: s.beneficiaries?.map((b) => ({
            id: b.id,
            name: b.name,
            picture: b.picture || "",
            school: b.school || "",
            location: b.location || "",
            class: b.class || "",
            notes: b.notes || "",
          })) || [],
        })) || [],
      });
    }
  }, [scholarship, reset]);

  // Auto-generate slug from title
  const generateSlug = (text: string): string => {
    return text
      .toLowerCase()
      .trim()
      .replace(/[^\w\s-]/g, "")
      .replace(/[\s_-]+/g, "-")
      .replace(/^-+|-+$/g, "");
  };

  // Auto-generate slugs when titles change
  const translations = watch("translations") || [];
  const translationTitles = useMemo(
    () => translations.map((t) => t.title).join(","),
    [translations]
  );

  useEffect(() => {
    translations.forEach((translation, index) => {
      if (autoGenerateSlugs[translation.locale] && translation.title) {
        const newSlug = generateSlug(translation.title);
        if (translation.slug !== newSlug) {
          setValue(`translations.${index}.slug`, newSlug);
        }
      }
    });
  }, [translationTitles, autoGenerateSlugs, translations, setValue]);

  // Enable auto-generation when title is first entered
  const handleTitleChange = (index: number, locale: string, value: string) => {
    if (!autoGenerateSlugs[locale] && value) {
      setAutoGenerateSlugs((prev) => ({ ...prev, [locale]: true }));
    }
    setValue(`translations.${index}.title`, value);
  };

  const onSubmit = async (data: ScholarshipEditFormData) => {
    setValidationError("");
    try {
      await updateScholarshipMutation.mutateAsync({
        id: scholarshipId,
        scholarshipData: data,
      });
      navigate(`/${currentLanguage}/admin/scholarships`);
    } catch (error) {
      console.error("Failed to update scholarship:", error);
      if (error && typeof error === "object" && "response" in error) {
        const axiosError = error as {
          response?: { data?: { errors?: Record<string, string[]> } };
        };
        if (axiosError.response?.data?.errors) {
          const errors = axiosError.response.data.errors;
          const firstError = Object.values(errors).flat()[0];
          setValidationError(
            firstError || t("admin.scholarships.errorUpdating")
          );
        } else {
          setValidationError(t("admin.scholarships.errorUpdating"));
        }
      } else if (error instanceof Error) {
        setValidationError(
          error.message || t("admin.scholarships.errorUpdating")
        );
      } else {
        setValidationError(t("admin.scholarships.errorUpdating"));
      }
    }
  };

  const addSession = () => {
    appendSession({
      academic_year: "",
      is_active: false,
      gallery: [],
      sponsors: [{ name: "", title: "", bio: "", picture: "", is_main: true }],
      beneficiaries: [],
    });
  };

  const removeSessionAt = (index: number) => {
    if (sessionFields.length > 1) {
      removeSession(index);
    }
  };

  const addTranslation = () => {
    appendTranslation({ locale: "", title: "", description: "", aim: "", eligibility: "", offers: "", slug: "" });
  };

  const removeTranslationAt = (index: number) => {
    if (translationFields.length > 1) {
      removeTranslation(index);
    }
  };

  const [activeSessionTab, setActiveSessionTab] = useState("session-0");

  if (isLoadingScholarship) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50/30 to-purple-50/30 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 p-6 flex items-center justify-center">
        <div className="text-center">
          <div className="w-10 h-10 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400 font-medium">{t("common.loading")}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50/30 to-purple-50/30 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 p-6">
      <div className="max-w-6xl mx-auto space-y-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-center gap-4 mb-6"
        >
          <Button
            variant="outline"
            type="button"
            onClick={() => navigate(`/${currentLanguage}/admin/scholarships`)}
            className="flex items-center gap-2 hover:bg-gray-100 dark:hover:bg-gray-800"
          >
            <ArrowLeft className="w-4 h-4" />
            {t("common.back")}
          </Button>
          <div className="flex-1">
            <h1 className="text-3xl font-extrabold text-gray-900 dark:text-white bg-clip-text text-transparent bg-gradient-to-r from-blue-600 to-purple-600">
              {t("admin.scholarships.editScholarship")}
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mt-1">
              {t("admin.scholarships.editScholarshipDescription")}
            </p>
          </div>
        </motion.div>

        {/* Validation Error */}
        {validationError && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 flex items-start gap-3"
          >
            <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
            <p className="text-red-600 dark:text-red-400 flex-1">
              {validationError}
            </p>
          </motion.div>
        )}

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          {/* Basic Info & Academic Years */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Sidebar: Status & Academic Years */}
            <div className="lg:col-span-1 space-y-6">
              <Card className="bg-white dark:bg-gray-800 border-0 shadow-lg">
                <CardHeader className="bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 border-b border-gray-200 dark:border-gray-700">
                  <CardTitle className="text-lg font-bold flex items-center gap-2">
                    <Info className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                    {t("admin.scholarships.basicInfo")}
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-6 space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="status" className="text-sm font-semibold flex items-center gap-2">
                      <GraduationCap className="w-4 h-4 text-green-600 dark:text-green-400" />
                      {t("admin.scholarships.status")} *
                    </Label>
                    <select
                      {...register("status")}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                    >
                      <option value="active">{t("scholarships.status.active")}</option>
                      <option value="inactive">{t("scholarships.status.inactive")}</option>
                      <option value="expired">{t("scholarships.status.expired")}</option>
                    </select>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-white dark:bg-gray-800 border-0 shadow-lg">
                <CardHeader className="bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 border-b border-gray-200 dark:border-gray-700">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg font-bold flex items-center gap-2">
                      <Calendar className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                      {t("admin.scholarships.academicYears")}
                    </CardTitle>
                    <Button type="button" variant="outline" size="sm" onClick={addSession}>
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="pt-6 space-y-4">
                  {sessionFields.map((field, index) => (
                    <div key={field.id} className="flex items-center gap-2 p-3 bg-gray-50 dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700">
                      <div className="flex-1 space-y-2">
                        <Input
                          {...register(`sessions.${index}.academic_year`)}
                          placeholder="e.g. 2023/2024"
                          className="h-8 text-sm"
                        />
                        <div className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            {...register(`sessions.${index}.is_active`)}
                            id={`session-active-${index}`}
                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                          <Label htmlFor={`session-active-${index}`} className="text-xs">
                            Active Session
                          </Label>
                        </div>
                      </div>
                      {sessionFields.length > 1 && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removeSessionAt(index)}
                          className="text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  ))}
                </CardContent>
              </Card>
            </div>

            {/* Main Content: Sessions (Sponsors & Beneficiaries) */}
            <div className="lg:col-span-2 space-y-6">
              <Tabs value={activeSessionTab} onValueChange={setActiveSessionTab} className="w-full">
                <TabsList className="w-full justify-start overflow-x-auto bg-transparent border-b border-gray-200 dark:border-gray-700 rounded-none h-auto p-0 gap-4">
                  {sessionFields.map((field, index) => (
                    <TabsTrigger
                      key={field.id}
                      value={`session-${index}`}
                      className="rounded-none border-b-2 border-transparent data-[state=active]:border-blue-600 data-[state=active]:bg-transparent px-4 py-2 font-semibold transition-all"
                    >
                      {watch(`sessions.${index}.academic_year`) || `Year ${index + 1}`}
                    </TabsTrigger>
                  ))}
                </TabsList>

                {sessionFields.map((field, index) => (
                  <TabsContent key={field.id} value={`session-${index}`} className="mt-6 space-y-6 animate-in fade-in slide-in-from-bottom-2">
                    {/* Sponsors for this session */}
                    <Card className="bg-white dark:bg-gray-800 border-0 shadow-lg">
                      <CardHeader className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 border-b border-gray-200 dark:border-gray-700">
                        <div className="flex items-center justify-between">
                          <CardTitle className="text-lg font-bold flex items-center gap-2">
                            <Trophy className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                            {t("admin.scholarships.sponsors")} ({watch(`sessions.${index}.academic_year`)})
                          </CardTitle>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              const sponsors = watch(`sessions.${index}.sponsors`) || [];
                              setValue(`sessions.${index}.sponsors`, [...sponsors, { name: "", title: "", bio: "", picture: "", is_main: false }]);
                            }}
                          >
                            <Plus className="w-4 h-4 mr-2" />
                            Add Sponsor
                          </Button>
                        </div>
                      </CardHeader>
                      <CardContent className="pt-6 space-y-6">
                        {(watch(`sessions.${index}.sponsors`) || []).map((_, sIndex) => (
                          <div key={sIndex} className="p-4 border border-gray-200 dark:border-gray-700 rounded-xl space-y-4 bg-gray-50/50 dark:bg-gray-900/50">
                            <div className="flex justify-between items-center">
                              <h5 className="font-bold flex items-center gap-2">
                                <span className="w-6 h-6 rounded-full bg-blue-600 text-white text-xs flex items-center justify-center">
                                  {sIndex + 1}
                                </span>
                                Sponsor
                              </h5>
                              <div className="flex items-center gap-2">
                                <div className="flex items-center gap-2 mr-4">
                                  <input
                                    type="checkbox"
                                    {...register(`sessions.${index}.sponsors.${sIndex}.is_main`)}
                                    id={`main-sponsor-${index}-${sIndex}`}
                                    onChange={(e) => {
                                      if (e.target.checked) {
                                        const sponsors = watch(`sessions.${index}.sponsors`) || [];
                                        sponsors.forEach((_, i) => {
                                          if (i !== sIndex) setValue(`sessions.${index}.sponsors.${i}.is_main`, false);
                                        });
                                      }
                                      setValue(`sessions.${index}.sponsors.${sIndex}.is_main`, e.target.checked);
                                    }}
                                  />
                                  <Label htmlFor={`main-sponsor-${index}-${sIndex}`} className="text-xs">Main Sponsor</Label>
                                </div>
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => {
                                    const sponsors = watch(`sessions.${index}.sponsors`) || [];
                                    setValue(`sessions.${index}.sponsors`, sponsors.filter((_, i) => i !== sIndex));
                                  }}
                                  className="text-red-500"
                                >
                                  <X className="w-4 h-4" />
                                </Button>
                              </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <div className="space-y-2">
                                <Label className="text-xs font-bold">Name *</Label>
                                <Input {...register(`sessions.${index}.sponsors.${sIndex}.name`)} placeholder="Sponsor name" />
                              </div>
                              <div className="space-y-2">
                                <Label className="text-xs font-bold">Title</Label>
                                <Input {...register(`sessions.${index}.sponsors.${sIndex}.title`)} placeholder="e.g. CEO of Company" />
                              </div>
                            </div>
                            <div className="space-y-2">
                              <Label className="text-xs font-bold">Bio</Label>
                              <textarea
                                {...register(`sessions.${index}.sponsors.${sIndex}.bio`)}
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-sm min-h-[80px]"
                                placeholder="Short bio..."
                              />
                            </div>
                            <div className="space-y-2">
                              <Label className="text-xs font-bold">Picture</Label>
                              <ImageUpload
                                onUpload={async (files) => {
                                  const file = files[0];
                                  if (!file) return [];
                                  return new Promise((resolve) => {
                                    const reader = new FileReader();
                                    reader.onload = (e) => {
                                      const res = e.target?.result as string;
                                      setValue(`sessions.${index}.sponsors.${sIndex}.picture`, res);
                                      resolve([res]);
                                    };
                                    reader.readAsDataURL(file);
                                  });
                                }}
                                onChange={(val) => {
                                  const url = Array.isArray(val) ? val[0] || "" : val || "";
                                  setValue(`sessions.${index}.sponsors.${sIndex}.picture`, url);
                                }}
                                value={watch(`sessions.${index}.sponsors.${sIndex}.picture`) || ""}
                                multiple={false}
                                maxFiles={1}
                              />
                            </div>
                          </div>
                        ))}
                      </CardContent>
                    </Card>

                    {/* Beneficiaries for this session */}
                    <Card className="bg-white dark:bg-gray-800 border-0 shadow-lg">
                      <CardHeader className="bg-gradient-to-r from-pink-50 to-orange-50 dark:from-pink-900/20 dark:to-orange-900/20 border-b border-gray-200 dark:border-gray-700">
                        <div className="flex items-center justify-between">
                          <CardTitle className="text-lg font-bold flex items-center gap-2">
                            <Users className="w-5 h-5 text-pink-600 dark:text-pink-400" />
                            {t("admin.scholarships.beneficiaries")} ({watch(`sessions.${index}.academic_year`)})
                          </CardTitle>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              const beneficiaries = watch(`sessions.${index}.beneficiaries`) || [];
                              setValue(`sessions.${index}.beneficiaries`, [...beneficiaries, { name: "", picture: "", school: "", location: "", class: "", notes: "" }]);
                            }}
                          >
                            <Plus className="w-4 h-4 mr-2" />
                            Add Student
                          </Button>
                        </div>
                      </CardHeader>
                      <CardContent className="pt-6 space-y-6">
                        {(watch(`sessions.${index}.beneficiaries`) || []).map((_, bIndex) => (
                          <div key={bIndex} className="p-4 border border-gray-200 dark:border-gray-700 rounded-xl space-y-4 bg-gray-50/5 dark:bg-gray-900/5">
                            <div className="flex justify-between items-center">
                              <h5 className="font-bold flex items-center gap-2">
                                <span className="w-6 h-6 rounded-full bg-pink-600 text-white text-xs flex items-center justify-center">
                                  {bIndex + 1}
                                </span>
                                Beneficiary
                              </h5>
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                  const beneficiaries = watch(`sessions.${index}.beneficiaries`) || [];
                                  setValue(`sessions.${index}.beneficiaries`, beneficiaries.filter((_, i) => i !== bIndex));
                                }}
                                className="text-red-500"
                              >
                                <X className="w-4 h-4" />
                              </Button>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <div className="space-y-2">
                                <Label className="text-xs font-bold">Name *</Label>
                                <Input {...register(`sessions.${index}.beneficiaries.${bIndex}.name`)} placeholder="Student name" />
                              </div>
                              <div className="space-y-2">
                                <Label className="text-xs font-bold">School</Label>
                                <Input {...register(`sessions.${index}.beneficiaries.${bIndex}.school`)} placeholder="School name" />
                              </div>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <div className="space-y-2">
                                <Label className="text-xs font-bold">Location</Label>
                                <Input {...register(`sessions.${index}.beneficiaries.${bIndex}.location`)} placeholder="City, Country" />
                              </div>
                              <div className="space-y-2">
                                <Label className="text-xs font-bold">Class / Grade</Label>
                                <Input {...register(`sessions.${index}.beneficiaries.${bIndex}.class`)} placeholder="e.g. Grade 10" />
                              </div>
                            </div>
                            <div className="space-y-2">
                              <Label className="text-xs font-bold">Notes</Label>
                              <textarea
                                {...register(`sessions.${index}.beneficiaries.${bIndex}.notes`)}
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-sm min-h-[60px]"
                                placeholder="Extra info..."
                              />
                            </div>
                            <div className="space-y-2">
                              <Label className="text-xs font-bold">Picture</Label>
                              <ImageUpload
                                onUpload={async (files) => {
                                  const file = files[0];
                                  if (!file) return [];
                                  return new Promise((resolve) => {
                                    const reader = new FileReader();
                                    reader.onload = (e) => {
                                      const res = e.target?.result as string;
                                      setValue(`sessions.${index}.beneficiaries.${bIndex}.picture`, res);
                                      resolve([res]);
                                    };
                                    reader.readAsDataURL(file);
                                  });
                                }}
                                onChange={(val) => {
                                  const url = Array.isArray(val) ? val[0] || "" : val || "";
                                  setValue(`sessions.${index}.beneficiaries.${bIndex}.picture`, url);
                                }}
                                value={watch(`sessions.${index}.beneficiaries.${bIndex}.picture`) || ""}
                                multiple={false}
                                maxFiles={1}
                              />
                            </div>
                          </div>
                        ))}
                      </CardContent>
                    </Card>

                    {/* Gallery for this session */}
                    <Card className="bg-white dark:bg-gray-800 border-0 shadow-lg">
                      <CardHeader className="bg-gradient-to-r from-green-50 to-teal-50 dark:from-green-900/20 dark:to-teal-900/20 border-b border-gray-200 dark:border-gray-700">
                        <CardTitle className="text-lg font-bold flex items-center gap-2">
                          <svg className="w-5 h-5 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                          {t("admin.scholarships.gallery", "Gallery")} ({watch(`sessions.${index}.academic_year`)})
                        </CardTitle>
                        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                          Upload photos from award ceremonies and events
                        </p>
                      </CardHeader>
                      <CardContent className="pt-6">
                        <ImageUpload
                          onUpload={async (files) => {
                            const uploadedUrls: string[] = [];
                            for (const file of files) {
                              const reader = new FileReader();
                              const base64 = await new Promise<string>((resolve) => {
                                reader.onload = (e) => resolve(e.target?.result as string);
                                reader.readAsDataURL(file);
                              });
                              uploadedUrls.push(base64);
                            }
                            const currentGallery = watch(`sessions.${index}.gallery`) || [];
                            setValue(`sessions.${index}.gallery`, [...currentGallery, ...uploadedUrls]);
                            return uploadedUrls;
                          }}
                          onChange={(val) => {
                            const urls = Array.isArray(val) ? val : (val ? [val] : []);
                            setValue(`sessions.${index}.gallery`, urls);
                          }}
                          value={watch(`sessions.${index}.gallery`) || []}
                          multiple={true}
                          maxFiles={20}
                        />
                        {(watch(`sessions.${index}.gallery`) || []).length === 0 && (
                          <p className="text-center text-gray-500 py-4 italic text-sm">No gallery images added for this session.</p>
                        )}
                      </CardContent>
                    </Card>
                  </TabsContent>
                ))}
              </Tabs>
            </div>
          </div>

          {/* Translations */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
          >
            <Card className="bg-white dark:bg-gray-800 border-0 shadow-lg">
              <CardHeader className="bg-gradient-to-r from-amber-50 to-yellow-50 dark:from-amber-900/20 dark:to-yellow-900/20 border-b border-gray-200 dark:border-gray-700">
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2 text-xl font-bold text-gray-900 dark:text-white">
                    <FileText className="w-5 h-5 text-amber-600 dark:text-amber-400" />
                    {t("admin.scholarships.translations")}
                  </CardTitle>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={addTranslation}
                    className="hover:bg-gray-100 dark:hover:bg-gray-700"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    {t("admin.scholarships.addTranslation")}
                  </Button>
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  {t("admin.scholarships.translationsHelp")}
                </p>
              </CardHeader>
              <CardContent className="pt-6">
                <div className="space-y-6">
                  {translationFields.map((field, index) => {
                    const translation = translations[index];
                    const currentLocale =
                      translation?.locale || field.locale || "";
                    return (
                      <motion.div
                        key={field.id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.5 + index * 0.1 }}
                        className="border-2 border-gray-200 dark:border-gray-700 rounded-xl p-6 space-y-4 bg-gradient-to-br from-white to-gray-50/50 dark:from-gray-800 dark:to-gray-900/50"
                      >
                        <div className="flex items-center justify-between">
                          <h4 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
                            <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-gradient-to-r from-blue-500 to-purple-500 text-white text-sm font-bold">
                              {currentLocale.toUpperCase() || index + 1}
                            </span>
                            {currentLocale === "en"
                              ? "English"
                              : currentLocale === "fr"
                                ? "Français"
                                : t("admin.scholarships.translation")}{" "}
                            {index + 1}
                          </h4>
                          {translationFields.length > 1 && (
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => removeTranslationAt(index)}
                              className="hover:bg-red-50 dark:hover:bg-red-900/20 hover:border-red-300 dark:hover:border-red-700"
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          )}
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label
                              htmlFor={`translations.${index}.locale`}
                              className="text-sm font-semibold"
                            >
                              {t("admin.scholarships.locale")} *
                            </Label>
                            <select
                              {...register(`translations.${index}.locale`)}
                              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            >
                              <option value="en">English</option>
                              <option value="fr">Français</option>
                            </select>
                            {errors.translations?.[index]?.locale && (
                              <p className="text-sm text-red-600 dark:text-red-400">
                                {(errors.translations as any)[index]?.locale?.message}
                              </p>
                            )}
                          </div>

                          <div className="space-y-2">
                            <Label
                              htmlFor={`translations.${index}.slug`}
                              className="flex items-center gap-2 text-sm font-semibold"
                            >
                              {t("admin.scholarships.slug")} *
                              <div title={t("admin.scholarships.slugHelp")}>
                                <HelpCircle className="w-3 h-3 text-gray-400 cursor-help" />
                              </div>
                            </Label>
                            <div className="relative">
                              <Input
                                {...register(`translations.${index}.slug`)}
                                placeholder="scholarship-slug"
                                className="w-full font-mono text-sm"
                                onChange={(e) => {
                                  setValue(
                                    `translations.${index}.slug`,
                                    e.target.value
                                  );
                                  setAutoGenerateSlugs((prev) => ({
                                    ...prev,
                                    [currentLocale]: true,
                                  }));
                                }}
                              />
                            </div>
                            {errors.translations?.[index]?.slug && (
                              <p className="text-sm text-red-600 dark:text-red-400">
                                {(errors.translations as any)[index]?.slug?.message}
                              </p>
                            )}
                          </div>
                        </div>

                        <div className="space-y-2">
                          <Label
                            htmlFor={`translations.${index}.title`}
                            className="flex items-center gap-2 text-sm font-semibold"
                          >
                            {t("admin.scholarships.title")} *
                            <div title={t("admin.scholarships.titleHelp")}>
                              <HelpCircle className="w-3 h-3 text-gray-400 cursor-help" />
                            </div>
                          </Label>
                          <Input
                            {...register(`translations.${index}.title`)}
                            onChange={(e) =>
                              handleTitleChange(
                                index,
                                currentLocale,
                                e.target.value
                              )
                            }
                            placeholder={t(
                              "admin.scholarships.titlePlaceholder"
                            )}
                            className="w-full"
                          />
                          {errors.translations?.[index]?.title && (
                            <p className="text-sm text-red-600 dark:text-red-400">
                              {(errors.translations as any)[index]?.title?.message}
                            </p>
                          )}
                        </div>

                        <div className="space-y-2">
                          <Label
                            htmlFor={`translations.${index}.description`}
                            className="text-sm font-semibold"
                          >
                            {t("admin.scholarships.description")}
                          </Label>
                          <Controller
                            name={`translations.${index}.description`}
                            control={control}
                            render={({ field }) => (
                              <ReactQuill
                                theme="snow"
                                value={field.value || ""}
                                onChange={field.onChange}
                                modules={quillModules}
                                className="bg-white dark:bg-gray-700 min-h-[200px]"
                                placeholder={t("admin.scholarships.descriptionPlaceholder")}
                              />
                            )}
                          />
                          {errors.translations?.[index]?.description && (
                            <p className="text-sm text-red-600 dark:text-red-400">
                              {(errors.translations as any)[index]?.description?.message}
                            </p>
                          )}
                        </div>
                        <div className="space-y-4 pt-4 border-t border-gray-100 dark:border-gray-800">
                          <div className="space-y-2">
                            <Label className="text-sm font-semibold">Aim</Label>
                            <Controller
                              name={`translations.${index}.aim`}
                              control={control}
                              render={({ field }) => (
                                <ReactQuill
                                  theme="snow"
                                  value={field.value || ""}
                                  onChange={field.onChange}
                                  modules={quillModules}
                                  className="bg-white dark:bg-gray-700 min-h-[150px]"
                                  placeholder="Scholarship aim..."
                                />
                              )}
                            />
                          </div>
                          <div className="space-y-2">
                            <Label className="text-sm font-semibold">Eligibility</Label>
                            <Controller
                              name={`translations.${index}.eligibility`}
                              control={control}
                              render={({ field }) => (
                                <ReactQuill
                                  theme="snow"
                                  value={field.value || ""}
                                  onChange={field.onChange}
                                  modules={quillModules}
                                  className="bg-white dark:bg-gray-700 min-h-[150px]"
                                  placeholder="Eligibility criteria..."
                                />
                              )}
                            />
                          </div>
                          <div className="space-y-2">
                            <Label className="text-sm font-semibold">Offers</Label>
                            <Controller
                              name={`translations.${index}.offers`}
                              control={control}
                              render={({ field }) => (
                                <ReactQuill
                                  theme="snow"
                                  value={field.value || ""}
                                  onChange={field.onChange}
                                  modules={quillModules}
                                  className="bg-white dark:bg-gray-700 min-h-[150px]"
                                  placeholder="What's offered..."
                                />
                              )}
                            />
                          </div>
                        </div>
                      </motion.div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {/* Form Actions */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            className="flex justify-end gap-4 pt-6 border-t border-gray-200 dark:border-gray-700"
          >
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate(`/${currentLanguage}/admin/scholarships`)}
              className="px-6"
            >
              {t("common.cancel")}
            </Button>
            <Button
              type="submit"
              disabled={updateScholarshipMutation.isPending}
              className="px-8 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white shadow-lg hover:shadow-xl transition-all duration-200"
            >
              {updateScholarshipMutation.isPending ? (
                <span className="flex items-center gap-2">
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  {t("common.updating")}
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4" />
                  {t("admin.scholarships.update")}
                </span>
              )}
            </Button>
          </motion.div>
        </form>
      </div>
    </div>
  );
};

ScholarshipEdit.displayName = "ScholarshipEdit";

export default ScholarshipEdit;
